import { useState, type ReactNode } from "react";
import { NavLink } from "react-router-dom";
import "./side-navigation.style.scss";
import logoNibjar from "../../assets/logo-white-NIBJAR.png";

// components
import TopNav from "../top-navigation/top-navigation.component";

// icons
import {
	FaChevronRight,
	FaCog,
	FaHistory,
	FaRegFileAlt,
	FaRegListAlt,
	FaSignOutAlt,
	FaTh,
	FaUserFriends,
	FaUserTie,
} from "react-icons/fa";

import { useLogout } from "../../queries/auth.queries";

// queries
import { useActiveExternalApp } from "../../queries/company.queries";

interface MenuItem {
	path: string;
	name: string;
	icon: ReactNode;
	gap?: boolean;
	line?: boolean;
}

interface SidebarProps {
	children?: ReactNode;
}

const Sidebar = ({ children }: SidebarProps) => {
	const { billModel } = useActiveExternalApp();
	const isBillCreationBlocked = billModel === "A";
	const [isSidenavOpen, setIsSidenavOpen] = useState(false);

	const logoutMutation = useLogout();
	const menuItem: MenuItem[] = isBillCreationBlocked
		? [
				{
					path: "/",
					name: "Dashboard",
					icon: <FaTh size={16} />,
					gap: true,
				},
				{
					path: "/invoice/bills",
					name: "Invoice Bills",
					icon: <FaRegListAlt size={16} />,
					gap: true,
				},
				{
					path: "/customers",
					name: "Billing Clients",
					icon: <FaUserFriends size={18} />,
				},
				{
					path: "/employees",
					name: "Employees",
					icon: <FaUserTie size={16} />,
				},
		  ]
		: [
				{
					path: "/",
					name: "Dashboard",
					icon: <FaTh size={16} />,
					gap: true,
				},
				{
					path: "/invoice/create",
					name: "Create Invoice",
					icon: <FaRegFileAlt size={18} />,
				},
				{
					path: "/invoice/bills",
					name: "Invoice Bills",
					icon: <FaRegListAlt size={16} />,
					gap: true,
				},
				{
					path: "/customers",
					name: "Billing Clients",
					icon: <FaUserFriends size={18} />,
				},
				{
					path: "/employees",
					name: "Employees",
					icon: <FaUserTie size={16} />,
				},

				// {
				// 	path: "/analytics",
				// 	name: "Analytics",
				// 	icon: <FaRegChartBar />,
				// },
				// {
				// 	path: "/test",
				// 	name: "Comment",
				// 	icon: <FaCommentAlt />,
				// },
				// {
				// 	path: "/invoice/template",
				// 	name: "Invoice Template",
				// 	icon: <FaShoppingBag />,
				// },
		  ];

	const handleLogout = () => {
		logoutMutation.mutate();
	};

	return (
		<div className="screen-wrapper">
			<div
				style={{ width: isSidenavOpen ? "220px" : "80px" }}
				className="sidebar-container"
			>
				<div
					className={`sidebar-toggler  ${
						isSidenavOpen ? "toggle-rotate" : ""
					}`}
					onClick={() => setIsSidenavOpen(!isSidenavOpen)}
				>
					<FaChevronRight />
				</div>
				<div className="sidebar-container-inner">
					<div className="sidebar-nav-links">
						{menuItem.map((item, index) => (
							<div key={index}>
								{item.line && (
									<hr style={{ marginBottom: "24px" }} />
								)}
								<NavLink
									to={item.path}
									className={`nav-link ${
										item.gap ? "link-gap" : ""
									} ${!isSidenavOpen ? "tooltip-visible" : ""}`}
									data-tooltip={item?.name}
									data-tooltip-pos="right"
									data-tooltip-length="medium"
								>
									<div className="link-icon">{item.icon}</div>
									<div
										className={`link-text ${
											!isSidenavOpen ? "text-hidden" : ""
										}`}
									>
										{item.name}
									</div>
								</NavLink>
							</div>
						))}
					</div>
					<div className="sidebar-bottom">
						<div
							className="sidebar-nav-links"
							style={{ borderBottom: "1px solid #d8d8d855" }}
						>
							<NavLink
								to="/activities"
								className={`nav-link nav-bottom ${
									!isSidenavOpen ? "tooltip-visible" : ""
								}`}
								data-tooltip={"Activity Monitor"}
								data-tooltip-pos="right"
								data-tooltip-length="medium"
							>
								<div className="link-icon">
									<FaHistory size={16} />
								</div>
								<div
									className={`link-text ${
										!isSidenavOpen ? "text-hidden" : ""
									}`}
								>
									Activity Monitor
								</div>
							</NavLink>
							<NavLink
								to="/company-setting"
								className={`nav-link nav-bottom ${
									!isSidenavOpen ? "tooltip-visible" : ""
								}`}
								data-tooltip={"Settings"}
								data-tooltip-pos="right"
								data-tooltip-length="medium"
							>
								<div className="link-icon">
									<FaCog size={16} />
								</div>
								<div
									className={`link-text ${
										!isSidenavOpen ? "text-hidden" : ""
									}`}
								>
									Settings
								</div>
							</NavLink>
							<div
								className="nav-link nav-bottom"
								onClick={handleLogout}
							>
								<div className="link-icon">
									<FaSignOutAlt size={16} />
								</div>
								<div
									className={`link-text ${
										!isSidenavOpen ? "text-hidden" : ""
									}`}
								>
									Logout
								</div>
							</div>
						</div>
						<div className="additional-container">
							<div className="additional-dark-text">
								<img src={logoNibjar} alt="logo" />
								<span
									className={`${
										!isSidenavOpen ? "text-hidden" : ""
									}`}
								>
									Nibjar Solutions
								</span>
							</div>
							{isSidenavOpen && (
								<div className="additional-light-text">
									<b>Version 1.0.0</b>
									<br />
									Copyright &copy; 2023 Nibjar Solutions Pvt.
									Ltd. All rights reserved.
								</div>
							)}
							<div className="additional-light-text"></div>
						</div>
					</div>
				</div>
			</div>

			<main className="main-wrapper">
				<TopNav isSidenavOpen={isSidenavOpen} />
				<div className="component-wrapper">{children}</div>
			</main>
		</div>
	);
};

export default Sidebar;
